import { useEffect, useMemo, useState } from 'react'
import { AccountLink, DrillValue } from '../components/Drill'
import { useTableControls, type Column } from '../lib/useTableControls'
import { TableHead } from '../components/TableControls'
import { DetailGrid, ExpandableRow, useOpenRow } from '../components/RowDetail'
import { dateTime } from '../lib/format'
import {
  listOrgs,
  getOrg,
  patchOrgMemberRole,
  removeOrgMember,
  type RemoteOrg,
  type RemoteOrgDetail,
  type RemoteOrgOffice,
  type RemoteAclGrant,
  type OrgRole
} from '../lib/adminClient'
import OrgAccessPanel from '../components/OrgAccessPanel'

// Organizations / RBAC. The list shows every org. Selecting one loads its
// members (with editable roles), its offices, and its ACL grants. The two
// last-owner guards on the server return 409; we surface those inline next to
// the member the admin tried to change, so the reason a change was refused is
// obvious rather than a silent no-op.

const ROLES: OrgRole[] = ['owner', 'admin', 'member', 'guest']

function fmt(ts: string | number | null): string {
  if (ts === null || ts === undefined || ts === '') return '—'
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return String(ts)
  return d.toISOString().slice(0, 10)
}

// Offices and ACL grants have server-defined shapes we do not assume. Pull out
// the primitive fields so whatever the server sends is shown faithfully.
function primitiveEntries(obj: Record<string, unknown>): Array<[string, string]> {
  return Object.entries(obj)
    .filter(([, v]) => v !== null && v !== undefined && (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'))
    .map(([k, v]) => [k, String(v)] as [string, string])
}

function officeLabel(o: RemoteOrgOffice): string {
  for (const key of ['name', 'title', 'label', 'city', 'id']) {
    const v = o[key]
    if (typeof v === 'string' && v.trim()) return v
  }
  return 'Office'
}

function roleTone(r: OrgRole): string {
  if (r === 'owner') return 'bg-accent/20 text-accent'
  if (r === 'admin') return 'bg-indigo-500/15 text-indigo-300'
  if (r === 'guest') return 'bg-white/5 text-slate-400'
  return 'bg-white/5 text-slate-300'
}

// What each role can do, for a member's detail.
const ROLE_MEANING: Record<OrgRole, string> = {
  owner: 'Owner: full control. An organization always keeps at least one.',
  admin: 'Admin: manages members and settings.',
  member: 'Member: works in the organization.',
  guest: 'Guest: sees only what is shared with them.'
}

const fullTime = (ts: string | number | null): string | null => (ts === null || ts === undefined || ts === '' ? null : dateTime(ts))

function memberName(displayName: string | null, handle: string | null, email: string | null, accountId: string): string {
  return displayName || (handle ? `@${handle}` : email || accountId)
}

function GrantCard({ grant }: { grant: RemoteAclGrant }): JSX.Element {
  const entries = primitiveEntries(grant)
  return (
    <div className="rounded-lg border border-white/5 bg-white/[0.02] p-3 text-[11px]">
      {entries.length === 0 ? (
        <span className="text-slate-500">Grant with no readable fields.</span>
      ) : (
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {entries.map(([k, v]) => (
            <span key={k} className="text-slate-300">
              <span className="text-slate-500">{k}</span> {v}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

function OrgDetail({ orgId, onBack }: { orgId: string; onBack: () => void }): JSX.Element {
  const [detail, setDetail] = useState<RemoteOrgDetail | null>(null)
  const memberOpen = useOpenRow()
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  // Per-member inline error (keyed by accountId) for 409 last-owner refusals.
  const [memberError, setMemberError] = useState<Record<string, string>>({})
  // Whether the org-level Access & licensing panel is open.
  const [accessOpen, setAccessOpen] = useState(false)

  async function load(): Promise<void> {
    setState('loading')
    const res = await getOrg(orgId)
    if (res.ok) {
      setDetail({ org: res.data.org, members: res.data.members, offices: res.data.offices, acl: res.data.acl })
      setState('ready')
    } else {
      setError(res.error)
      setState('error')
    }
  }

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId])

  function clearMemberError(accountId: string): void {
    setMemberError((prev) => {
      if (!prev[accountId]) return prev
      const next = { ...prev }
      delete next[accountId]
      return next
    })
  }

  async function onChangeRole(accountId: string, role: OrgRole): Promise<void> {
    setBusy(accountId)
    clearMemberError(accountId)
    const res = await patchOrgMemberRole(orgId, accountId, role)
    setBusy(null)
    if (res.ok) {
      setDetail({ org: res.data.org, members: res.data.members, offices: res.data.offices, acl: res.data.acl })
    } else {
      const msg =
        res.status === 409
          ? 'Refused: an organization must keep at least one owner. Promote another member to owner first.'
          : res.error || 'Could not change role.'
      setMemberError((prev) => ({ ...prev, [accountId]: msg }))
    }
  }

  async function onRemove(accountId: string, label: string): Promise<void> {
    if (!window.confirm(`Remove ${label} from this organization?`)) return
    setBusy(accountId)
    clearMemberError(accountId)
    const res = await removeOrgMember(orgId, accountId)
    setBusy(null)
    if (res.ok) {
      setDetail({ org: res.data.org, members: res.data.members, offices: res.data.offices, acl: res.data.acl })
    } else {
      const msg =
        res.status === 409
          ? 'Refused: an organization must keep at least one owner. Promote another member to owner before removing this one.'
          : res.error || 'Could not remove member.'
      setMemberError((prev) => ({ ...prev, [accountId]: msg }))
    }
  }

  const backBar = (
    <button onClick={onBack} className="text-sm text-slate-400 hover:text-white mb-4">
      ← Back to organizations
    </button>
  )

  if (state === 'loading') {
    return (
      <div>
        {backBar}
        <div className="glass-card rounded-xl p-6 text-sm text-slate-400">Loading organization…</div>
      </div>
    )
  }
  if (state === 'error' || !detail) {
    return (
      <div>
        {backBar}
        <div className="glass-card rounded-xl p-6 text-sm text-red-300">Could not load organization: {error}</div>
      </div>
    )
  }

  const { org, members, offices, acl } = detail
  const aclOnOrg = acl?.onOrg ?? []
  const aclToOrg = acl?.toOrg ?? []

  return (
    <div>
      {backBar}

      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400">Organization</div>
          <h1 className="text-2xl font-semibold mt-1 flex items-center gap-2">
            {org.name}
            {org.personal && (
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/5 text-slate-400">
                Personal
              </span>
            )}
          </h1>
          <div className="text-sm text-slate-400 mt-1">
            {org.slug} ·{' '}
            <DrillValue to={{ set: 'orgMembers', filters: { org: org.id } }} label={`Members of ${org.name}`} expect={{ key: 'count', value: org.memberCount }}>
              {org.memberCount} {org.memberCount === 1 ? 'member' : 'members'}
            </DrillValue>{' '}
            · created {fmt(org.createdAt)} ·{' '}
            <DrillValue to={{ set: 'orgs', filters: { id: org.id } }} label={org.name}>the record</DrillValue>
          </div>
        </div>
        <button
          onClick={() => setAccessOpen(true)}
          className="shrink-0 px-3 py-1.5 rounded-md text-sm border border-white/10 text-slate-200 hover:bg-white/5"
        >
          Access &amp; licensing
        </button>
      </div>

      {accessOpen && (
        <OrgAccessPanel
          org={{ id: org.id, name: org.name, slug: org.slug }}
          onClose={() => setAccessOpen(false)}
        />
      )}

      <div className="glass-card rounded-xl overflow-hidden mb-6">
        <div className="px-4 pt-4 text-[11px] uppercase tracking-[0.16em] text-slate-400">Members</div>
        {members.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">No members.</div>
        ) : (
          <table className="admin-table mt-3">
            <thead>
              <tr>
                <th>Member</th>
                <th>Role</th>
                <th>Added</th>
                <th className="text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => {
                const label = memberName(m.displayName, m.handle, m.email, m.accountId)
                const err = memberError[m.accountId]
                return (
                  <ExpandableRow
                    key={m.accountId}
                    open={memberOpen.isOpen(m.accountId)}
                    onToggle={() => memberOpen.toggle(m.accountId)}
                    colSpan={4}
                    testId={`org-member-${m.accountId}`}
                    detail={() => (
                      <DetailGrid
                        facts={[
                          ['Name', m.displayName],
                          ['Handle', m.handle ? `@${m.handle}` : null],
                          ['Email', m.email],
                          ['Account id', <AccountLink id={m.accountId}><span className="font-mono">{m.accountId}</span></AccountLink>],
                          ['Role', ROLE_MEANING[m.role] ?? m.role],
                          ['Added', fullTime(m.addedAt)],
                          !!err && ['Last change refused', err]
                        ]}
                      />
                    )}
                  >
                    <td>
                      <div className="font-medium text-slate-100">{label}</div>
                      <div className="text-[11px] text-slate-500">{m.email ?? m.accountId}</div>
                      {err && <div className="mt-1 text-[11px] text-red-300">{err}</div>}
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${roleTone(m.role)}`}>
                          {m.role}
                        </span>
                        <select
                          value={m.role}
                          disabled={busy === m.accountId}
                          onChange={(e) => void onChangeRole(m.accountId, e.target.value as OrgRole)}
                          className="bg-black/30 border border-white/10 rounded-md px-2 py-1 text-xs focus:outline-none focus:border-accent disabled:opacity-50"
                        >
                          {ROLES.map((r) => (
                            <option key={r} value={r}>
                              {r}
                            </option>
                          ))}
                        </select>
                      </div>
                    </td>
                    <td className="text-slate-400 text-xs">{fmt(m.addedAt)}</td>
                    <td className="text-right">
                      <button
                        onClick={() => void onRemove(m.accountId, label)}
                        disabled={busy === m.accountId}
                        className="px-2.5 py-1 rounded-md text-xs border border-red-500/30 text-red-300 hover:bg-red-500/10 disabled:opacity-50"
                      >
                        {busy === m.accountId ? '…' : 'Remove'}
                      </button>
                    </td>
                  </ExpandableRow>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="glass-card rounded-xl p-5">
          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400 mb-3">Offices</div>
          {offices.length === 0 ? (
            <div className="text-sm text-slate-500">No offices for this organization.</div>
          ) : (
            <div className="space-y-2">
              {offices.map((o, i) => {
                const entries = primitiveEntries(o).filter(([k]) => k !== 'name' && k !== 'title' && k !== 'label')
                return (
                  <div key={(o.id as string) ?? i} className="rounded-lg border border-white/5 bg-white/[0.02] p-3">
                    <div className="text-sm font-medium text-slate-100">{officeLabel(o)}</div>
                    {entries.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[11px]">
                        {entries.map(([k, v]) => (
                          <span key={k} className="text-slate-300">
                            <span className="text-slate-500">{k}</span> {v}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <div className="glass-card rounded-xl p-5">
          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400 mb-3">ACL grants</div>
          <div className="mb-3">
            <div className="text-xs text-slate-400 mb-1.5">On this org ({aclOnOrg.length})</div>
            {aclOnOrg.length === 0 ? (
              <div className="text-sm text-slate-500">No grants on this org.</div>
            ) : (
              <div className="space-y-2">
                {aclOnOrg.map((g, i) => (
                  <GrantCard key={i} grant={g} />
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="text-xs text-slate-400 mb-1.5">To other orgs ({aclToOrg.length})</div>
            {aclToOrg.length === 0 ? (
              <div className="text-sm text-slate-500">No grants to other orgs.</div>
            ) : (
              <div className="space-y-2">
                {aclToOrg.map((g, i) => (
                  <GrantCard key={i} grant={g} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const ORG_COLUMNS: Column<RemoteOrg>[] = [
  { key: 'name', label: 'Organization', value: (o) => o.name, filter: 'text' },
  { key: 'slug', label: 'Slug', value: (o) => o.slug, filter: 'text' },
  { key: 'type', label: 'Type', value: (o) => (o.personal ? 'Personal' : 'Team') },
  { key: 'members', label: 'Members', value: (o) => o.memberCount, numeric: true, className: 'text-right' },
  { key: 'created', label: 'Created', value: (o) => String(o.createdAt ?? '') },
  { key: 'action', label: '', filter: 'none', sortable: false, className: 'text-right' }
]

export default function Organizations(): JSX.Element {
  const [orgs, setOrgs] = useState<RemoteOrg[] | null>(null)
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void listOrgs().then((res) => {
      if (cancelled) return
      if (res.ok) {
        setOrgs(res.data.orgs)
        setState('ready')
      } else {
        setError(res.error)
        setState('error')
      }
    })
    return () => {
      cancelled = true
    }
  }, [])

  const visible = useMemo(() => {
    const all = orgs ?? []
    const q = query.trim().toLowerCase()
    if (!q) return all
    return all.filter(
      (o) => o.name.toLowerCase().includes(q) || o.slug.toLowerCase().includes(q) || o.id.toLowerCase().includes(q)
    )
  }, [orgs, query])

  // Before the early return below, so the hook order never changes.
  const t = useTableControls(visible, ORG_COLUMNS, 'orgs')
  const rowOpen = useOpenRow()

  if (selected) {
    return <OrgDetail orgId={selected} onBack={() => setSelected(null)} />
  }

  const header = (
    <div className="mb-6">
      <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400">RBAC</div>
      <h1 className="text-2xl font-semibold mt-1">
        Organizations
        {orgs ? <span className="text-slate-400 text-base ml-2 font-normal">{visible.length} of {orgs.length}</span> : null}
      </h1>
      <div className="text-sm text-slate-400 mt-1">
        Every organization, its members and roles, offices, and access grants. Click a row for its summary, or Manage to change it.
      </div>
    </div>
  )

  if (state !== 'ready') {
    return (
      <div>
        {header}
        <div className="glass-card rounded-xl p-6 text-sm text-slate-400">
          {state === 'loading' ? 'Loading organizations…' : `Could not load organizations: ${error}`}
        </div>
      </div>
    )
  }

  return (
    <div>
      {header}

      <div className="glass-card rounded-xl p-4 mb-4 flex flex-wrap gap-3">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search name, slug, or org id…"
          className="flex-1 min-w-[260px] bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-accent"
        />
      </div>

      {visible.length === 0 ? (
        <div className="glass-card rounded-xl p-6 text-sm text-slate-400">
          {(orgs ?? []).length === 0 ? 'No organizations yet.' : 'No organizations match your search.'}
        </div>
      ) : (
        <div className="glass-card rounded-xl overflow-hidden">
          <table className="admin-table">
            <TableHead t={t} />
            <tbody>
              {/* A row opens its summary underneath; Manage opens the organization. */}
              {t.rows.map((o) => (
                <ExpandableRow
                  key={o.id}
                  open={rowOpen.isOpen(o.id)}
                  onToggle={() => rowOpen.toggle(o.id)}
                  colSpan={ORG_COLUMNS.length}
                  testId={`org-row-${o.id}`}
                  detail={() => (
                    <DetailGrid
                      facts={[
                        ['Name', o.name],
                        ['Slug', o.slug],
                        ['Organization id', <DrillValue to={{ set: 'orgs', filters: { id: o.id } }} label={o.name}><span className="font-mono">{o.id}</span></DrillValue>],
                        ['Type', o.personal ? 'Personal: the space every account gets for itself' : 'Team: an organization people were invited to'],
                        ['Members', <DrillValue to={{ set: 'orgMembers', filters: { org: o.id } }} label={`Members of ${o.name}`} expect={{ key: 'count', value: o.memberCount }}>{o.memberCount}</DrillValue>],
                        ['Created', fullTime(o.createdAt)]
                      ]}
                    >
                      <button className="btn-ghost text-xs" onClick={() => setSelected(o.id)} data-testid={`org-manage-${o.id}`}>
                        Members, offices and access
                      </button>
                    </DetailGrid>
                  )}
                >
                  <td>
                    <div className="font-medium text-slate-100">{o.name}</div>
                    <div className="text-[11px] text-slate-500">{o.id}</div>
                  </td>
                  <td className="text-slate-300">{o.slug}</td>
                  <td>
                    {o.personal ? (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/5 text-slate-400">
                        Personal
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-accent/20 text-accent">
                        Team
                      </span>
                    )}
                  </td>
                  <td className="text-right tabular-nums text-slate-300">
                    <DrillValue to={{ set: 'orgMembers', filters: { org: o.id } }} label={`Members of ${o.name}`} expect={{ key: 'count', value: o.memberCount }} testId={`org-members-records-${o.id}`}>{o.memberCount}</DrillValue>
                  </td>
                  <td className="text-slate-400 text-xs">{fmt(o.createdAt)}</td>
                  <td className="text-right">
                    <button
                      className="btn-ghost text-xs"
                      onClick={(e) => {
                        e.stopPropagation()
                        setSelected(o.id)
                      }}
                    >
                      Manage
                    </button>
                  </td>
                </ExpandableRow>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
